// src/components/staff/HoursAccountModal.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import HoursAccountService from '../../services/HoursAccountService';
import { HoursAdjustmentType, HoursAdjustmentTypeLabels } from '../../models/HoursAccountModel';
import { formatSignedHours } from '../../utils/DurationCalculator';
import '../../styles/hours-account-modal.css';

/**
 * Matches a partial or complete signed decimal number as the user is
 * typing it — e.g. "", "-", "-2", "-2.", "-2.5", "+1", "1,5". Used to
 * gate keystrokes on the two hours inputs so that in-progress values
 * (which parseFloat would reject as NaN) aren't discarded mid-entry.
 */
const PARTIAL_SIGNED_NUMBER_PATTERN = /^[-+]?\d*[.,]?\d*$/;

/**
 * Parses a user-entered hours string that may use either a period or a
 * German-style comma as the decimal separator (matches the field hint
 * "Dezimalwerte möglich: 1,5 = 1 Std. 30 Min."). Returns NaN for anything
 * that isn't a parseable number, same contract as parseFloat.
 */
function parseHoursInput(value) {
  if (value === null || value === undefined) return NaN;
  return parseFloat(String(value).trim().replace(',', '.'));
}

/** Rounds to 2 decimals, guarding against floating-point diff artifacts
 * (e.g. 0.30000000000000004) when comparing two already-rounded server values.
 */
function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * HoursAccountModal
 *
 * Modal for managing an employee's hours account. Three manager actions:
 *   - Settings tab: edit the opening balance
 *   - Adjustments tab: add or remove manual adjustments
 *   - Recalculate tab: re-run a closed month's calculation from source data
 *     (clock-ins, schedules, holidays, vacation, sick leave) via the
 *     hoursAccountNightlyRecalc Cloud Function's guarded historical-recalc
 *     branch. This *replaces* the stored worked/target/balance for that
 *     month — it is deliberately kept separate from "Adjustments", which
 *     only ever adds a manual delta on top of whatever was calculated.
 *
 * The adjustments list and monthlyBalances reflect the hoursAccount prop
 * from StaffDetailPage. After any write the modal stays open (adjustments,
 * recalc) or closes with onClose(true) (opening balance save / delete),
 * which triggers hoursRefreshTrigger in the parent and causes
 * HoursAccountCard to re-fetch.
 *
 * Adjustment add/delete and month recalculation additionally call
 * onAccountChanged with the freshly updated account (returned directly by
 * the service call) so the modal's own list and the card behind it stay in
 * sync without requiring the modal to close or a page reload.
 *
 * Props:
 *   isOpen            {boolean}
 *   onClose           {function}  Called with (didChange: boolean)
 *   staffId           {string}
 *   companyId         {string}
 *   year              {string}
 *   hoursAccount      {HoursAccount|null}  Passed from StaffDetailPage
 *   onAccountChanged  {function}  Called with (updatedAccount: HoursAccount)
 *                                 after a successful adjustment add/delete
 *                                 or a confirmed month recalculation
 *   staffName         {string}   Optional. Used in the recalc confirmation
 *                                 copy ("X — June 2026 will be recalculated
 *                                 ..."). Falls back to a generic label if
 *                                 not provided.
 */
function HoursAccountModal({
  isOpen,
  onClose,
  staffId,
  companyId,
  year,
  hoursAccount,
  onAccountChanged,
  staffName = null,
}) {
  // Explicitly load 'common' alongside 'staff' — this component calls
  // t('common:cancel') / t('common:deleting'), which requires the
  // 'common' namespace to actually be loaded rather than relying on it
  // having been mounted by some other component first.
  const { t } = useTranslation(['staff', 'common']);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [formData, setFormData] = useState({ openingBalance: 0 });
  const [adjustmentForm, setAdjustmentForm] = useState({
    type:  HoursAdjustmentType.ADMIN_ADJUSTMENT,
    hours: 0,
    note:  '',
  });
  const [showAddAdjustment, setShowAddAdjustment]   = useState(false);
  const [saving, setSaving]                         = useState(false);
  const [error, setError]                           = useState(null);
  const [activeTab, setActiveTab]                   = useState('settings');
  const [showDeleteConfirm, setShowDeleteConfirm]   = useState(false);
  const [isDeleting, setIsDeleting]                 = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [isDeletingAdjustment, setIsDeletingAdjustment] = useState(false);

  // Recalculate tab
  const [recalcTargetMonth, setRecalcTargetMonth] = useState(null); // "MM" currently showing a preview
  const [recalcPreview, setRecalcPreview]         = useState(null); // { current, computed } for recalcTargetMonth
  const [previewingMonth, setPreviewingMonth]     = useState(null); // "MM" whose preview fetch is in flight
  const [isPreviewing, setIsPreviewing]           = useState(false);
  const [isRecalculating, setIsRecalculating]     = useState(false);
  const [recalcSuccess, setRecalcSuccess]         = useState(null); // { key, ytdBalance }

  const isBusy =
    saving ||
    isDeleting ||
    isDeletingAdjustment ||
    isPreviewing ||
    isRecalculating;

  // Tracks the opening balance as it was when the modal opened, so we can
  // tell whether the settings tab has unsaved edits (used to guard
  // accidental overlay-click / Escape dismissal).
  const initialOpeningBalanceRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Reset form when modal opens or year / account changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      const opening = hoursAccount?.openingBalance || 0;
      setFormData({ openingBalance: opening });
      initialOpeningBalanceRef.current = opening;
      setAdjustmentForm({
        type:  HoursAdjustmentType.ADMIN_ADJUSTMENT,
        hours: 0,
        note:  '',
      });
      setError(null);
      setShowAddAdjustment(false);
      setActiveTab('settings');
      setShowDeleteConfirm(false);
      setPendingDeleteIndex(null);
      setRecalcTargetMonth(null);
      setRecalcPreview(null);
      setPreviewingMonth(null);
      setRecalcSuccess(null);
    }
  }, [isOpen, hoursAccount, year]);

  // ---------------------------------------------------------------------------
  // Body scroll lock while modal is open
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Unsaved-changes detection
  // ---------------------------------------------------------------------------

  const hasUnsavedOpeningBalance =
    activeTab === 'settings' &&
    (parseHoursInput(formData.openingBalance || 0) || 0) !== initialOpeningBalanceRef.current;

  const hasUnsavedAdjustmentDraft =
    showAddAdjustment &&
    ((parseHoursInput(adjustmentForm.hours) || 0) !== 0 || adjustmentForm.note.trim() !== '');

  // An open recalc preview is deliberately excluded here — it's a read-only
  // dry run with nothing written yet, so dismissing it loses nothing and
  // shouldn't block closing the modal.
  const hasUnsavedChanges = hasUnsavedOpeningBalance || hasUnsavedAdjustmentDraft;

  // ---------------------------------------------------------------------------
  // Close handling (shared by overlay click, X button, Escape key)
  // ---------------------------------------------------------------------------

  const requestClose = useCallback(() => {
    if (isBusy) return;
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(t('hoursAccount.modal.discardChangesConfirm'));
      if (!confirmed) return;
    }
    onClose(false);
  }, [isBusy, hasUnsavedChanges, onClose, t]);

  // Escape-to-close
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, requestClose]);

  // ---------------------------------------------------------------------------
  // Tab switching (clears any in-progress recalc preview)
  // ---------------------------------------------------------------------------

  const handleTabChange = (tab) => {
    if (isBusy) return;
    setActiveTab(tab);
    setRecalcTargetMonth(null);
    setRecalcPreview(null);
    setRecalcSuccess(null);
  };

  // ---------------------------------------------------------------------------
  // Form input handlers
  // ---------------------------------------------------------------------------

  /**
   * Opening balance field. Kept as type="text" (see JSX below) rather than
   * type="number" so the browser never runs its native value-sanitization
   * on a controlled re-render — that sanitization silently blanks out
   * in-progress values like "-" that aren't yet a complete valid number.
   * PARTIAL_SIGNED_NUMBER_PATTERN gates keystrokes so only characters that
   * could plausibly form a signed decimal are ever accepted.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (!PARTIAL_SIGNED_NUMBER_PATTERN.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Adjustment form fields. The "hours" field gets the same partial-number
   * gating as handleInputChange above; "type" and "note" pass through
   * unchanged. Previously this coerced every keystroke through
   * parseFloat(value) || 0, which reset the field to 0 the instant a user
   * typed a lone "-", making it impossible to type a negative value
   * directly — this is the fix for that.
   */
  const handleAdjustmentInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'hours') {
      if (!PARTIAL_SIGNED_NUMBER_PATTERN.test(value)) return;
      setAdjustmentForm((prev) => ({ ...prev, hours: value }));
      return;
    }

    setAdjustmentForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const validateForm = useCallback(() => {
    const val = parseHoursInput(formData.openingBalance);
    if (isNaN(val)) {
      return t('hoursAccount.validation.openingBalanceInvalid');
    }
    return null;
  }, [formData.openingBalance, t]);

  // ---------------------------------------------------------------------------
  // Save opening balance
  // ---------------------------------------------------------------------------

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await HoursAccountService.setOpeningBalance(
        companyId,
        staffId,
        year,
        parseHoursInput(formData.openingBalance)
      );
      onClose(true);
    } catch (err) {
      setError(err.message || t('hoursAccount.errorSavingAccount'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Add adjustment
  // ---------------------------------------------------------------------------

  const handleAddAdjustment = async () => {
    const hoursValue = parseHoursInput(adjustmentForm.hours);

    if (!hoursValue) {
      setError(t('hoursAccount.validation.adjustmentHoursRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updatedAccount = await HoursAccountService.addAdjustment(
        companyId,
        staffId,
        year,
        { ...adjustmentForm, hours: hoursValue }
      );
      setAdjustmentForm({
        type:  HoursAdjustmentType.ADMIN_ADJUSTMENT,
        hours: 0,
        note:  '',
      });
      setShowAddAdjustment(false);
      onAccountChanged?.(updatedAccount);
    } catch (err) {
      setError(err.message || t('hoursAccount.errorAddingAdjustment'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete adjustment (inline two-step confirm)
  // ---------------------------------------------------------------------------

  const handleDeleteAdjustmentRequest = (index) => {
    setPendingDeleteIndex(index);
    setError(null);
  };

  const handleCancelAdjustmentDelete = () => {
    setPendingDeleteIndex(null);
  };

  const handleConfirmAdjustmentDelete = async () => {
    if (pendingDeleteIndex === null) return;

    setIsDeletingAdjustment(true);
    setError(null);

    try {
      const updatedAccount = await HoursAccountService.deleteAdjustment(
        companyId,
        staffId,
        year,
        pendingDeleteIndex
      );
      setPendingDeleteIndex(null);
      onAccountChanged?.(updatedAccount);
    } catch (err) {
      setError(err.message || t('hoursAccount.errorDeletingAdjustment'));
    } finally {
      setIsDeletingAdjustment(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete entire account
  // ---------------------------------------------------------------------------

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await HoursAccountService.deleteHoursAccount(companyId, staffId, year);
      onClose(true);
    } catch (err) {
      setError(err.message || t('hoursAccount.errorDeletingAccount'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Recalculate month (preview -> confirm)
  // ---------------------------------------------------------------------------

  const handlePreviewRecalc = async (monthKeyStr) => {
    setError(null);
    setRecalcSuccess(null);
    setPreviewingMonth(monthKeyStr);
    setIsPreviewing(true);

    try {
      const monthNum = parseInt(monthKeyStr, 10);
      const result = await HoursAccountService.recalculateMonth(
        companyId, staffId, year, monthNum, { preview: true }
      );
      setRecalcTargetMonth(monthKeyStr);
      setRecalcPreview(result);
    } catch (err) {
      setError(err.message || t('hoursAccount.recalc.error'));
    } finally {
      setIsPreviewing(false);
      setPreviewingMonth(null);
    }
  };

  const handleCancelRecalcPreview = () => {
    setRecalcTargetMonth(null);
    setRecalcPreview(null);
  };

  const handleConfirmRecalc = async () => {
    if (!recalcTargetMonth) return;

    setError(null);
    setIsRecalculating(true);

    try {
      const monthNum = parseInt(recalcTargetMonth, 10);
      const updatedAccount = await HoursAccountService.recalculateMonth(
        companyId, staffId, year, monthNum, { preview: false }
      );
      setRecalcSuccess({
        key:        recalcTargetMonth,
        ytdBalance: updatedAccount.ytdBalance,
      });
      setRecalcTargetMonth(null);
      setRecalcPreview(null);
      onAccountChanged?.(updatedAccount);
    } catch (err) {
      setError(err.message || t('hoursAccount.recalc.error'));
    } finally {
      setIsRecalculating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns a CSS class name based on the signed balance value.
   * Duplicated from HoursAccountCard intentionally — see cross-cutting constraints.
   */
  const getBalanceColor = (balance) => {
    if (Math.abs(balance) < 0.5) return 'balance-neutral';
    if (balance > 0) return 'balance-positive';
    return 'balance-negative';
  };

  /**
   * Returns the translated adjustment type label, falling back to the
   * English label map if the i18n key is missing.
   */
  const getAdjustmentTypeName = (type) => {
    const key = `hoursAccount.adjustmentTypes.${type}`;
    const translated = t(key);
    return translated !== key ? translated : HoursAdjustmentTypeLabels[type];
  };

  /**
   * Formats a Date to a short German-locale date string (no time component).
   */
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day:   '2-digit',
      month: '2-digit',
      year:  'numeric',
    });
  };

  /**
   * Formats a "MM" monthlyBalances key + the modal's `year` prop into a
   * human-readable month/year label, e.g. "06" -> "Juni 2026".
   */
  const formatMonthYear = (monthKeyStr) => {
    const monthIndex = parseInt(monthKeyStr, 10) - 1;
    return new Date(Number(year), monthIndex, 1).toLocaleDateString('de-DE', {
      month: 'long',
      year:  'numeric',
    });
  };

  const canDelete = hoursAccount !== null;

  // Only closed months can be recalculated — open months recalculate
  // continuously via the nightly job and have no stable "before" to diff.
  const closedMonths = hoursAccount
    ? Object.entries(hoursAccount.monthlyBalances || {})
        .filter(([, m]) => m.closed === true)
        .sort(([a], [b]) => a.localeCompare(b))
    : [];

  // ---------------------------------------------------------------------------
  // Early exit when closed
  // ---------------------------------------------------------------------------

  if (!isOpen) return null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className="modal-overlay"
      onClick={requestClose}
    >
      <div
        className="modal-container hours-account-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hours-account-modal-title"
      >

        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2 id="hours-account-modal-title">
              {t('hoursAccount.modal.title', { year })}
            </h2>
          </div>
          <button
            className="close-btn"
            onClick={requestClose}
            disabled={isBusy}
            aria-label={t('common:close')}
          >
            &#x2715;
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="error-banner" role="alert">
            <span aria-hidden="true">&#9888;</span>
            <span>{error}</span>
          </div>
        )}

        {/* Tabs */}
        {hoursAccount && (
          <div className="modal-tabs" role="tablist">
            <button
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('settings')}
              disabled={isBusy}
              role="tab"
              aria-selected={activeTab === 'settings'}
            >
              {t('hoursAccount.modal.accountSettings')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'adjustments' ? 'active' : ''}`}
              onClick={() => handleTabChange('adjustments')}
              disabled={isBusy}
              role="tab"
              aria-selected={activeTab === 'adjustments'}
            >
              {t('hoursAccount.adjustments')}
              {hoursAccount?.adjustments?.length > 0 && (
                <span className="tab-badge">
                  {hoursAccount.adjustments.length}
                </span>
              )}
            </button>
            <button
              className={`tab-btn ${activeTab === 'recalc' ? 'active' : ''}`}
              onClick={() => handleTabChange('recalc')}
              disabled={isBusy}
              role="tab"
              aria-selected={activeTab === 'recalc'}
            >
              {t('hoursAccount.recalc.tabTitle')}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="modal-body">

          {/* ----------------------------------------------------------------
              Settings tab
          ---------------------------------------------------------------- */}
          {activeTab === 'settings' && (
            <div className="settings-section">

              {/* Opening balance input */}
              <div className="form-group">
                <label htmlFor="openingBalance">
                  {t('hoursAccount.openingBalance')}
                </label>
                <div className="input-with-unit">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="openingBalance"
                    name="openingBalance"
                    value={formData.openingBalance}
                    onChange={handleInputChange}
                    placeholder="0"
                    disabled={isBusy}
                  />
                  <span className="unit">{t('hoursAccount.hoursUnit')}</span>
                </div>
                <p className="field-hint">
                  {t('hoursAccount.openingBalanceHint')}
                </p>
              </div>

              {/* Read-only YTD summary */}
              {hoursAccount && (
                <div className="current-summary">
                  <h4>{t('hoursAccount.currentAccountSummary')}</h4>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">
                        {t('hoursAccount.ytdBalance')}:
                      </span>
                      <span
                        className={`value ${getBalanceColor(hoursAccount.ytdBalance)}`}
                      >
                        {formatSignedHours(hoursAccount.ytdBalance)}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="label">
                        {t('hoursAccount.lastCalculated')}:
                      </span>
                      <span className="value">
                        {hoursAccount.lastCalculatedAt
                          ? formatDate(hoursAccount.lastCalculatedAt)
                          : t('hoursAccount.neverCalculated')}
                      </span>
                    </div>
                  </div>
                  <p className="field-hint">
                    {t('hoursAccount.ytdBalanceReadOnly')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ----------------------------------------------------------------
              Adjustments tab
          ---------------------------------------------------------------- */}
          {activeTab === 'adjustments' && (
            <div className="adjustments-section">

              {/* Section header */}
              <div className="section-header">
                <p className="section-title">
                  {t('hoursAccount.adjustmentHistory')}
                </p>
                {!showAddAdjustment && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddAdjustment(true);
                      setError(null);
                    }}
                    disabled={isBusy}
                  >
                    {t('hoursAccount.addAdjustment')}
                  </button>
                )}
              </div>

              {/* Inline add-adjustment form */}
              {showAddAdjustment && (
                <div className="add-adjustment-form">
                  <p className="form-title">{t('hoursAccount.newAdjustment')}</p>

                  {/* Type */}
                  <div className="form-group">
                    <label htmlFor="adj-type">
                      {t('hoursAccount.adjustmentType')}
                    </label>
                    <select
                      id="adj-type"
                      name="type"
                      value={adjustmentForm.type}
                      onChange={handleAdjustmentInputChange}
                      disabled={isBusy}
                    >
                      {Object.values(HoursAdjustmentType).map((type) => (
                        <option key={type} value={type}>
                          {getAdjustmentTypeName(type)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Hours */}
                  <div className="form-group">
                    <label htmlFor="adj-hours">
                      {t('hoursAccount.adjustmentHours')}
                    </label>
                    <div className="input-with-unit">
                      <input
                        type="text"
                        inputMode="decimal"
                        id="adj-hours"
                        name="hours"
                        value={adjustmentForm.hours}
                        onChange={handleAdjustmentInputChange}
                        placeholder="0"
                        disabled={isBusy}
                      />
                      <span className="unit">{t('hoursAccount.hoursUnit')}</span>
                    </div>
                    <p className="field-hint">
                      {t('hoursAccount.adjustmentHoursHint')}
                    </p>
                  </div>

                  {/* Note */}
                  <div className="form-group">
                    <label htmlFor="adj-note">
                      {t('hoursAccount.adjustmentNote')}
                    </label>
                    <textarea
                      id="adj-note"
                      name="note"
                      value={adjustmentForm.note}
                      onChange={handleAdjustmentInputChange}
                      placeholder={t('hoursAccount.adjustmentNotePlaceholder')}
                      disabled={isBusy}
                    />
                  </div>

                  {/* Form actions */}
                  <div className="form-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddAdjustment(false);
                        setError(null);
                        setAdjustmentForm({
                          type:  HoursAdjustmentType.ADMIN_ADJUSTMENT,
                          hours: 0,
                          note:  '',
                        });
                      }}
                      disabled={isBusy}
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddAdjustment}
                      disabled={isBusy}
                    >
                      {saving
                        ? t('hoursAccount.modal.saving')
                        : t('hoursAccount.addAdjustment')}
                    </button>
                  </div>
                </div>
              )}

              {/* Adjustment history list */}
              <div className="adjustment-list">
                {!hoursAccount?.adjustments?.length ? (
                  <p className="no-adjustments">
                    {t('hoursAccount.noAdjustments')}
                  </p>
                ) : (
                  hoursAccount.adjustments.map((adjustment, index) => (
                    <div className="adjustment-item" key={index}>

                      {/* Header row: meta + value */}
                      <div className="adjustment-header">
                        <div className="adjustment-meta">
                          <span className="adjustment-type">
                            {getAdjustmentTypeName(adjustment.type)}
                          </span>
                          <span className="adjustment-date">
                            {formatDate(adjustment.createdAt)}
                          </span>
                        </div>
                        <span
                          className={`adjustment-value ${
                            adjustment.hours >= 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {formatSignedHours(adjustment.hours)}
                        </span>
                      </div>

                      {/* Note */}
                      {adjustment.note && (
                        <p className="adjustment-note">{adjustment.note}</p>
                      )}

                      {/* Delete / inline confirm */}
                      {pendingDeleteIndex === index ? (
                        <div className="adjustment-delete-confirm">
                          <p className="confirm-label">
                            {t('hoursAccount.validation.confirmDeleteAdjustment')}
                          </p>
                          <div className="confirm-actions">
                            <button
                              className="btn btn-secondary"
                              onClick={handleCancelAdjustmentDelete}
                              disabled={isDeletingAdjustment}
                            >
                              {t('common:cancel')}
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={handleConfirmAdjustmentDelete}
                              disabled={isDeletingAdjustment}
                            >
                              {isDeletingAdjustment
                                ? t('common:deleting')
                                : t('hoursAccount.validation.deleteAdjustment')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="adjustment-delete-btn"
                          onClick={() => handleDeleteAdjustmentRequest(index)}
                          disabled={isBusy}
                        >
                          {t('hoursAccount.validation.deleteAdjustment')}
                        </button>
                      )}

                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------
              Recalculate tab
          ---------------------------------------------------------------- */}
          {activeTab === 'recalc' && (
            <div className="recalc-section">

              <p className="section-hint recalc-hint">
                {t('hoursAccount.recalc.hint')}
              </p>

              {recalcSuccess && (
                <div className="recalc-success-banner" role="status">
                  <span aria-hidden="true">&#10003;</span>
                  <span>
                    {t('hoursAccount.recalc.success', {
                      month:      formatMonthYear(recalcSuccess.key),
                      ytdBalance: formatSignedHours(recalcSuccess.ytdBalance),
                    })}
                  </span>
                </div>
              )}

              {closedMonths.length === 0 ? (
                <p className="no-adjustments">
                  {t('hoursAccount.recalc.onlyClosedMonths')}
                </p>
              ) : (
                <div className="recalc-month-list">
                  {closedMonths.map(([monthKeyStr, m]) => {
                    const isThisMonthPreviewing = recalcTargetMonth === monthKeyStr && recalcPreview;
                    const isThisMonthLoading = previewingMonth === monthKeyStr;

                    return (
                      <div className="recalc-month-item" key={monthKeyStr}>

                        <div className="recalc-month-header">
                          <span className="recalc-month-label">
                            {formatMonthYear(monthKeyStr)}
                          </span>
                          <span
                            className={`adjustment-value ${
                              m.balance >= 0 ? 'positive' : 'negative'
                            }`}
                          >
                            {formatSignedHours(m.balance)}
                          </span>
                        </div>

                        <div className="recalc-month-detail">
                          <span>
                            {t('hoursAccount.recalc.workedLabel')}: {formatSignedHours(m.worked)}
                          </span>
                          <span>
                            {t('hoursAccount.recalc.targetLabel')}: {formatSignedHours(m.target)}
                          </span>
                        </div>

                        {isThisMonthPreviewing ? (
                          <div className="recalc-preview-panel">
                            <p className="recalc-preview-title">
                              {t('hoursAccount.recalc.confirmTitle')}
                            </p>
                            <p className="recalc-preview-body">
                              {t('hoursAccount.recalc.confirmBody', {
                                staffName: staffName || t('hoursAccount.recalc.genericStaffLabel'),
                                month:     formatMonthYear(monthKeyStr),
                              })}
                            </p>

                            <div className="recalc-diff-grid">
                              <div className="recalc-diff-row recalc-diff-header">
                                <span />
                                <span>{t('hoursAccount.recalc.current')}</span>
                                <span>{t('hoursAccount.recalc.computed')}</span>
                                <span>{t('hoursAccount.recalc.delta')}</span>
                              </div>

                              {[
                                { field: 'worked',  labelKey: 'workedLabel' },
                                { field: 'target',  labelKey: 'targetLabel' },
                                { field: 'balance', labelKey: 'balanceLabel' },
                              ].map(({ field, labelKey }) => {
                                const currentVal  = recalcPreview.current?.[field] ?? 0;
                                const computedVal = recalcPreview.computed?.[field] ?? 0;
                                const delta = round2(computedVal - currentVal);

                                return (
                                  <div className="recalc-diff-row" key={field}>
                                    <span className="recalc-diff-label">
                                      {t(`hoursAccount.recalc.${labelKey}`)}
                                    </span>
                                    <span>{formatSignedHours(currentVal)}</span>
                                    <span>{formatSignedHours(computedVal)}</span>
                                    <span
                                      className={
                                        delta !== 0 ? (delta > 0 ? 'positive' : 'negative') : ''
                                      }
                                    >
                                      {delta !== 0 ? formatSignedHours(delta) : '\u2013'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="recalc-preview-actions">
                              <button
                                className="btn btn-secondary"
                                onClick={handleCancelRecalcPreview}
                                disabled={isRecalculating}
                              >
                                {t('common:cancel')}
                              </button>
                              <button
                                className="btn btn-primary"
                                onClick={handleConfirmRecalc}
                                disabled={isRecalculating}
                              >
                                {isRecalculating
                                  ? t('hoursAccount.modal.saving')
                                  : t('hoursAccount.recalc.button')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="btn btn-secondary recalc-month-btn"
                            onClick={() => handlePreviewRecalc(monthKeyStr)}
                            disabled={isBusy}
                          >
                            {isThisMonthLoading
                              ? t('hoursAccount.modal.saving')
                              : t('hoursAccount.recalc.button')}
                          </button>
                        )}

                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-left">
            {canDelete && !showDeleteConfirm && (
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isBusy}
              >
                {t('hoursAccount.deleteAccount')}
              </button>
            )}
          </div>

          <div className="footer-right">
            {showDeleteConfirm ? (
              <>
                <span className="delete-confirm-inline-label">
                  {t('hoursAccount.confirmDeleteWarning')}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {t('common:cancel')}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? t('common:deleting')
                    : t('hoursAccount.confirmDelete')}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={requestClose}
                  disabled={isBusy}
                >
                  {t('common:cancel')}
                </button>
                {activeTab === 'settings' && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isBusy}
                  >
                    {saving
                      ? t('hoursAccount.modal.saving')
                      : t('hoursAccount.modal.save')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default HoursAccountModal;